import { ReactNode, useEffect } from 'react';
import { useAuth as useClerkAuth, useOrganization, useSession } from '@clerk/react';
import { useLocation } from 'react-router-dom';

import {
  readPendingCompanyName,
  readPendingTenantId,
} from '@/api/tenants/linkClerkOrg';
import PrefillCreateOrganization from '@/component-library/widgets/PrefillCreateOrganization/PrefillCreateOrganization';
import { useResolvedPendingCompanyName } from '@/hooks/useResolvedPendingCompanyName';
import { isPathBCustomOrgConfirmEnabled } from '@/utils/auth/pathBOrgConfirm';

const LOG = '[ChurnSignal][PathB-Org]';

type Props = {
  source: 'sign-up' | 'sign-in';
  children: ReactNode;
};

function isChooseOrganizationTask(pathname: string): boolean {
  return pathname.includes('/tasks/choose-organization');
}

/**
 * Opt-in custom Path B org confirm (`REACT_APP_PATH_B_ORG_CONFIRM=custom`).
 * Only mounts Clerk org/session hooks on the choose-organization task so verify-email
 * OTP is not remounted by useOrganization while unsigned-in.
 */
export default function PathBOrgConfirmGate({ source, children }: Props) {
  const location = useLocation();

  if (!isPathBCustomOrgConfirmEnabled()) {
    return <>{children}</>;
  }

  if (!isChooseOrganizationTask(location.pathname)) {
    return <>{children}</>;
  }

  return <PathBOrgConfirmGateOnTask source={source}>{children}</PathBOrgConfirmGateOnTask>;
}

function PathBOrgConfirmGateOnTask({ source, children }: Props) {
  const location = useLocation();
  const { isLoaded, isSignedIn, userId } = useClerkAuth();
  const { session, isLoaded: isSessionLoaded } = useSession();
  const { organization, isLoaded: isOrgLoaded } = useOrganization();
  const { companyName, isResolving, hasPendingRegistration } = useResolvedPendingCompanyName();

  const showConfirm = hasPendingRegistration;

  useEffect(() => {
    console.info(LOG, `PathBOrgConfirmGate (${source})`, {
      mode: 'custom',
      pathname: location.pathname,
      isLoaded,
      isOrgLoaded,
      isSessionLoaded,
      isSignedIn,
      userId: userId ?? null,
      sessionId: session?.id ?? null,
      organizationId: organization?.id ?? null,
      hasPendingRegistration,
      showConfirm,
      willRender: showConfirm
        ? isResolving
          ? 'loading-company-name'
          : 'PrefillCreateOrganization'
        : 'children (Clerk widget)',
    });
  }, [
    source,
    location.pathname,
    isLoaded,
    isOrgLoaded,
    isSessionLoaded,
    isSignedIn,
    userId,
    session?.id,
    organization?.id,
    hasPendingRegistration,
    isResolving,
    showConfirm,
  ]);

  if (showConfirm) {
    if (isResolving) {
      return (
        <p className="py-8 text-center text-sm text-custom-darkSilver">Loading your company details…</p>
      );
    }
    return <PrefillCreateOrganization initialName={companyName} />;
  }

  return <>{children}</>;
}
